// Captures the browser's install prompt (Chrome, Edge, Android) so we can show
// our own "Install app" button. Must be imported before the page settles.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  notify();
});
addEventListener('appinstalled', () => {
  deferred = null;
  installed = true;
  notify();
});

let installed = false;
export const installState = () => ({ canPrompt: !!deferred, installed });
export const subscribeInstall = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  notify();
  return outcome === 'accepted';
}

const BROWSER_KEY = 'cr_use_browser';
/** Whether to show the app itself: when installed, or when the user chose the browser. */
export function shouldShowApp() {
  if (matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone) return true;
  try {
    return localStorage.getItem(BROWSER_KEY) === '1';
  } catch {
    return false;
  }
}
export function useBrowserAnyway() {
  try {
    localStorage.setItem(BROWSER_KEY, '1');
  } catch {
    /* ignore */
  }
}
